import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Warning, ArrowRight } from "@phosphor-icons/react";
import { DecisionDialog } from "@/components/DecisionDialog";
import { addToPrefsList } from "@/lib/prefsList";

const STEP_DESTINATIONS = {
  add_account: "/accounts",
  set_school_coa: "/students",
  invite_student: "/students",
};

export function NewBeneficiarySetupCard() {
  const qc = useQueryClient();
  const { selectStudent } = useAuth();
  const { data: status } = useQuery({ queryKey: ["onboarding-status"], queryFn: async () => (await api.get("/onboarding-status")).data });
  // Keeps the newest preferences on hand for the shared save below.
  useQuery({ queryKey: ["user-prefs"], queryFn: async () => (await api.get("/user-prefs")).data });

  const [dismissTarget, setDismissTarget] = useState(null); // the one beneficiary being dismissed
  // Cards dismissed on this screen: they go the moment the dismiss is
  // confirmed, not once the save comes back (review round 5). A failed save
  // brings the card back.
  const [gone, setGone] = useState(() => new Set());

  const confirmDismiss = async () => {
    if (!dismissTarget) return;
    const id = dismissTarget.id;
    setGone((g) => new Set(g).add(id));
    setDismissTarget(null);
    try {
      await addToPrefsList(qc, "dismissed_setup_beneficiaries", id);
    } catch (e) {
      setGone((g) => { const n = new Set(g); n.delete(id); return n; });
      throw e;
    }
    qc.invalidateQueries({ queryKey: ["onboarding-status"] });
  };

  const items = (status?.new_beneficiaries || []).filter((b) => !gone.has(b.id));
  if (!items.length) return null;

  // ZEN-784 (Maryse, 17 Oct 2026): reminders are per child — one card per
  // beneficiary, each dismissed on its own; never one card naming several.
  return (
    <>
      {items.map((b) => (
        <div key={b.id} className="mb-4 rounded-lg border border-[#D97706]/40 bg-[#FFFBEB] p-4" data-testid="new-beneficiary-setup-card" data-student={b.id}>
          <div className="mb-2 flex items-start justify-between gap-2">
            <div>
              <h3 className="text-sm font-bold text-[#92400E]" data-testid="new-beneficiary-setup-title">
                Finish setting up {b.name}
              </h3>
              <p className="text-xs text-[#92400E]/70">
                {b.missing_steps.length} step{b.missing_steps.length !== 1 ? "s" : ""} left before their expenses can be tracked.
              </p>
            </div>
          </div>
          <div className="space-y-1.5">
            {b.missing_steps.map((step) => (
              <div key={step.key} className="flex items-center justify-between rounded-md border border-[#D97706]/20 bg-white px-3 py-2 text-sm" data-testid={`new-setup-step-${b.id}-${step.key}`}>
                <div className="flex items-center gap-2">
                  <Warning size={14} weight="bold" className="shrink-0 text-[#D97706]" />
                  <span className="font-medium text-[#0A0A0A]">{step.label}</span>
                </div>
                <Link
                  to={STEP_DESTINATIONS[step.key] || "/students"}
                  onClick={() => selectStudent(b.id)}
                  className="flex items-center gap-0.5 text-xs font-semibold text-[#D97706] hover:underline"
                  data-testid={`new-setup-go-${b.id}-${step.key}`}
                >
                  Go <ArrowRight size={12} weight="bold" />
                </Link>
              </div>
            ))}
          </div>
          <div className="mt-3 flex justify-end">
            <button
              onClick={() => setDismissTarget(b)}
              className="text-xs font-semibold text-[#92400E]/60 hover:text-[#92400E] hover:underline"
              data-testid={`new-setup-dismiss-${b.id}`}
            >
              Dismiss for {b.name}
            </button>
          </div>
        </div>
      ))}
      <DecisionDialog
        open={dismissTarget !== null}
        onClose={() => setDismissTarget(null)}
        onConfirm={confirmDismiss}
        title="Dismiss setup reminder?"
        actionLabel="Dismiss"
        testid="dismiss-setup"
      >
        <p className="text-sm text-[#374151]">You can still set them up from the Beneficiaries page.</p>
      </DecisionDialog>
    </>
  );
}
